/**
 * Shows a Taste Profile question with related choices
 */

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { Link } from 'react-router'
import { push } from 'react-router-redux'
import classnames from 'classnames'

import { Tooltip, ProgressBar, AnimatedFlyingText } from 'components'
import Choice from 'environments/TasteProfileOnboarding/components/Choice'
import { selectQuestions, selectBrowser } from 'environments/TasteProfileOnboarding/selectors'
import { selectCurrentQuestion } from './selectors'
import { answerQuestion } from './actions'
import { clearResult } from '../PersonalTasteProfile/actions'
import styles from './question.css'
import escapeTexts from 'constants/escapeTexts'
import preventOrphans from 'utils/preventOrphans'

const noop = () => {
}
const INFO = String.fromCharCode(9432)

export class Question extends Component {
  constructor (props) {
    super(props)

    this.state = {
      selectedChoiceId: null
    }

    this.body = null

    this.onSelectChoice = this.onSelectChoice.bind(this)
    this.gotoNextQuestion = this.gotoNextQuestion.bind(this)
  }

  componentDidMount () {
    this.body = document.querySelector('body')
    this.disableBodyScroll()
  }

  componentWillUnmount () {
    this.body.setAttribute('style', '')
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0
  }

  render () {
    const {question, questions, nextUrl} = this.props
    const {label, choices, id, url, images} = question
    const questionIndex = this.props.questions.indexOf(question) + 1
    const eventAction = `Vraag ${questionIndex} / ${questions.length} - ${label}`
    const {selectedChoiceId} = this.state
    const isChoiceSelected = selectedChoiceId !== null
    const isLastQuestion = nextUrl === 'mijn-profiel'
    const done = isLastQuestion && isChoiceSelected
    const progress = Math.floor(((id - (isChoiceSelected ? 0 : 1)) / questions.length) * 100)
    return (
      <div className={styles.questionsWrapper}>
        <div
          className={classnames(styles.background,
            {
              [styles.slideInBottom]: !done && id === 1,
              [styles.slideOutTop]: done
            })}
          style={{animationDelay: done ? '750ms' : 0}}
          onAnimationEnd={done ? this.gotoNextQuestion : noop}
        />
        <Link
          className={classnames(
            styles.closeButton,
            {
              [styles.flyIn]: !done,
              [styles.flyOut]: done && isChoiceSelected
            }
          )}
          to={process.env.WEBSHOP_SMAAKPROFIELEN_PATH + '/doe-de-test/'}
        >
          Sluit test
        </Link>
        <div className={styles.wrapper}>
          <div className={styles.headerWrapper}>
            <AnimatedFlyingText
              visible={!isChoiceSelected}
              className={styles.h1}
              delay={id === 1 ? 250 : 0}
            >
              {preventOrphans(label)}
            </AnimatedFlyingText>
            <ProgressBar
              className={classnames(styles.progressBar, styles.circularProgressBar, {
                [styles.flyOut]: done && isChoiceSelected
              })}
              shape={'circle'}
              percent={progress}
              width={66}
              strokeWidth={4}
              trailWidth={4}
            />
          </div>
          <div className={styles.questionWrapper}>
            {choices.map((choice, index) => {
              const selected = choice.id === selectedChoiceId
              let delay = selected ? 280 : 120
              if (id === 1) {
                delay = 300 + (choice.id * 50)
              }
              const animationDelay = `${delay}ms`
              return (
                <Choice
                  key={choice.id}
                  questionUrl={url}
                  image={images[index]}
                  choiceObj={choice}
                  index={index}
                  clickHandler={this.onSelectChoice}
                  selected={selected}
                  onAnimationEnd={selected && !done ? this.gotoNextQuestion : noop}
                  eventAction={eventAction}
                  style={{animationDelay}}
                  className={classnames(
                    {
                      [styles.flyIn]: !isChoiceSelected,
                      [styles.flyOut]: isChoiceSelected
                    }
                  )}
                />
              )
            })}
          </div>
          <div className={styles.footer}>
            <Tooltip
              className={styles.escapeTooltip}
              style={{
                position: 'topCenter',
                width: '310px'
              }}
              trigger={(
                <button
                  className={classnames(styles.escapeButton, {
                    [styles.flyOut]: done && isChoiceSelected
                  })}
                >
                  Wat als ik dit niet lust? {INFO}
                </button>
              )}
            >
              {escapeTexts[id]}
            </Tooltip>
            <ProgressBar
              className={classnames(styles.progressBar, styles.linearProgressBar, {
                [styles.flyOut]: done && isChoiceSelected
              })}
              shape={'line'}
              percent={progress}
              strokeWidth={4}
              trailWidth={4}
            />
          </div>
        </div>
      </div>
    )
  }

  onSelectChoice (choice) {
    this.setState({
      selectedChoiceId: choice
    })
  }

  disableBodyScroll () {
    this.body.setAttribute('style', 'overflow: hidden')
    document.body.scrollTop = 0
    document.documentElement.scrollTop = 0
  }

  gotoNextQuestion () {
    const {question, saveAnswerQuestion, nextUrl} = this.props
    saveAnswerQuestion(question.id, this.state.selectedChoiceId)

    this.props.clearResult()
    this.props.navigateTo(`${process.env.WEBSHOP_SMAAKPROFIELEN_PATH}/doe-de-test/${nextUrl}`)
    this.setState({
      selectedChoiceId: null
    })
  }
}

Question.propTypes = {
  questions: PropTypes.array.isRequired,
  question: PropTypes.object.isRequired,
  saveAnswerQuestion: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
  nextUrl: PropTypes.string.isRequired,
  clearResult: PropTypes.func.isRequired
}

const mapStateToProps = createStructuredSelector({
  question: selectCurrentQuestion,
  questions: selectQuestions,
  browser: selectBrowser
})

function mapDispatchToProps (dispatch) {
  return {
    navigateTo: (url) => dispatch(push(url)),
    saveAnswerQuestion: (q, a) => dispatch(answerQuestion(q, a)),
    clearResult: () => dispatch(clearResult())
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Question)
